import { useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';

import { useReverseChargeLine } from '../../store/hooks';
import { formatCents } from '../v2/shared';

import { serverReasonOf } from './serverReason';

export interface ReverseChargeTarget {
  chargeLineId: string;
  amountCents: number;
  /**
   * Whether the line carried a payment — decides the consequence copy.
   *
   * OPTIONAL, and `undefined` means genuinely unknown rather than false.
   * Activity lines do not carry payment state: tone `'ok'` covers
   * WRITTEN_OFF as well as PAID (see `_build_activity_lines`), so the
   * per-line panel cannot assert paid-ness and must not infer it from a
   * display colour. When undefined the dialog states only what is true
   * in every reversible status (ESTIMATED, CONFIRMED, PAID).
   *
   * Surfaces that hold the real datum — the balance table's account rows
   * read it from `patientDueStatus` — keep passing it and keep the
   * sharper copy. Do not "tidy" this back into a tone branch.
   */
  hasAppliedPayment?: boolean;
  label: string;
  /** The encounter a per-line reversal came from, for attribution. */
  encounterId?: string;
}

interface Props {
  charge: ReverseChargeTarget;
  onClose: () => void;
  onReversed: (freedCents: number) => void;
}

/**
 * Confirm a reversal, naming what it does to the money (gdn-3187).
 *
 * Copy rules carried over from ReverseChargeAction, the drawer-era
 * implementation this replaces (gdn-2915/2957 lineage):
 *
 *  - After a reversal the biller's next question is "so where did that
 *    money go?" — answer it in the same breath and name the destination.
 *  - On a PAID line the payment does NOT come back; it becomes unapplied
 *    account credit. Saying "reversed" alone reads as "refunded", which
 *    is a different act with different consequences.
 *  - After a failure, always say whether anything was recorded.
 *
 * The server is the eligibility authority: adjudicated, invoiced and
 * ERA-netted lines are refused with a stated reason, and that reason is
 * rendered verbatim rather than paraphrased. A paraphrase would be the FE
 * inventing a diagnosis it does not hold.
 */
export const ReverseChargeDialog = ({ charge, onClose, onReversed }: Props) => {
  const { reverseChargeLine, isLoading } = useReverseChargeLine();
  const [refusal, setRefusal] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const busy = submitting || isLoading;

  const amount = formatCents(charge.amountCents);
  // Consequence-named per the gdn-3154 submit-button rule: the button
  // says what the click does, including where the money lands.
  //
  // The unknown case says "removed from the account", never "comes off
  // the balance" — a PAID line contributes nothing to the open balance,
  // so that phrasing is subtly false exactly where the money moved.
  // Every clause here is either unconditionally true or explicitly
  // conditional ("any payment already collected").
  const confirmLabel =
    charge.hasAppliedPayment === undefined
      ? `Reverse this ${amount} charge — any payment already collected stays as account credit`
      : charge.hasAppliedPayment
        ? `Reverse this ${amount} charge — the payment stays and becomes account credit`
        : `Reverse this ${amount} charge — it comes off the balance`;

  const handleConfirm = async () => {
    if (busy) return;
    setSubmitting(true);
    setRefusal(null);
    try {
      const result = await reverseChargeLine(charge.chargeLineId);
      if (result.success) {
        onReversed(result.data.amount_cents ?? 0);
        return;
      }
      setRefusal(
        serverReasonOf(result.error) ??
          'Could not reverse this charge — nothing was changed.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontSize: '1rem', fontWeight: 700 }}>Reverse charge</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          {charge.label} — {amount}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {charge.hasAppliedPayment === undefined
            ? 'Reversing removes this charge from the account. Any payment already collected against it stays on the account as unapplied credit, ready to apply to another visit or refund — it is not returned to the card.'
            : charge.hasAppliedPayment
              ? 'This charge is paid. Reversing it voids the charge and frees the payment as unapplied account credit — the money stays on the account, ready to apply to another visit or refund. It is not returned to the card.'
              : 'Reversing voids this charge and takes it off the balance.'}
        </Typography>
        {refusal && (
          // The server's own words. It knows why (adjudicated, already
          // invoiced, ERA-netted); the FE does not, and a friendlier
          // paraphrase would be a guess.
          <Alert severity="error" sx={{ mb: 1, fontSize: '0.8125rem' }}>
            {refusal}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleConfirm}
          disabled={busy}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          {busy ? 'Reversing…' : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * What to tell the biller after a reversal succeeds (gdn-3187/3190).
 *
 * Lives beside the dialog so the confirmation and the confirmation's
 * aftermath cannot drift apart — they answer the same question about the
 * same money, and the snackbar is the ONLY continuity the biller gets:
 * the reversed line vanishes on refetch, because the server omits VOID
 * lines by design.
 *
 * @param hasAppliedPayment `undefined` where the surface cannot know
 *   (activity lines carry no payment state). Unknown gets a conditional
 *   sentence — never "off the balance", which is subtly false for a PAID
 *   line since it contributes nothing to the open balance.
 * @param reversedCents the CHARGE amount. The route returns
 *   `abs(entry.amount_cents)`; the server does not report whether
 *   allocations were released, so this is not "freed" money.
 */
export const reversalMessage = (
  hasAppliedPayment: boolean | undefined,
  reversedCents: number,
): string => {
  if (hasAppliedPayment === undefined) {
    return 'Charge reversed — removed from the account; any payment collected against it is now unapplied account credit.';
  }
  return hasAppliedPayment && reversedCents > 0
    ? `Charge reversed — ${formatCents(reversedCents)} is now unapplied account credit, ready to apply to another visit or refund.`
    : 'Charge reversed — the charge is voided and off the balance.';
};
